import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarFiliais, criarFilial, atualizarFilial, deletarFilial } from '../../../services/montagemPalete';
import type { Filial } from '../../../types/montagemPalete';
import '../MontagemPaletes.css';

const MontagemPaleteFiliais: React.FC = () => {
  const navigate = useNavigate();
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Filial | null>(null);
  const [form, setForm] = useState({ cnpj: '', razao_social: '', nome_fantasia: '', telefone: '', email: '' });

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listarFiliais();
      if (res.success) setFiliais(res.filiais);
    } catch (error: any) {
      alert(error?.message || 'Erro ao carregar filiais');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleSalvar = async () => {
    try {
      if (!form.cnpj || !form.razao_social) {
        alert('CNPJ e Razão Social são obrigatórios');
        return;
      }
      if (editing) {
        await atualizarFilial(editing.id, form);
      } else {
        await criarFilial(form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ cnpj: '', razao_social: '', nome_fantasia: '', telefone: '', email: '' });
      carregar();
    } catch (error: any) {
      alert(error?.message || 'Erro ao salvar filial');
    }
  };

  const handleEditar = (filial: Filial) => {
    setEditing(filial);
    setForm({
      cnpj: filial.cnpj,
      razao_social: filial.razao_social,
      nome_fantasia: filial.nome_fantasia || '',
      telefone: filial.telefone || '',
      email: filial.email || ''
    });
    setShowForm(true);
  };

  const handleDeletar = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta filial?')) return;
    try {
      await deletarFilial(id);
      carregar();
    } catch (error: any) {
      alert(error?.message || 'Erro ao excluir filial');
    }
  };

  return (
    <div className="page-container">
      <button className="montagem-back-link" onClick={() => navigate('/montar-carga')}>
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            <i className="fas fa-building" style={{ marginRight: 8, color: '#3498db' }}></i>
            Filiais/Unidades ({filiais.length})
          </h3>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setEditing(null); setForm({ cnpj: '', razao_social: '', nome_fantasia: '', telefone: '', email: '' }); }}>
            <i className="fas fa-plus"></i> Nova Filial
          </button>
        </div>
        <div className="card-body">
          {showForm && (
            <div className="palete-form-inline" style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem' }}>{editing ? 'Editar Filial' : 'Nova Filial'}</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>CNPJ *</label>
                  <input type="text" className="form-control" value={form.cnpj} onChange={(e) => setForm(p => ({ ...p, cnpj: e.target.value }))} placeholder="00.000.000/0000-00" />
                </div>
                <div className="form-group">
                  <label>Razão Social *</label>
                  <input type="text" className="form-control" value={form.razao_social} onChange={(e) => setForm(p => ({ ...p, razao_social: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Nome Fantasia</label>
                  <input type="text" className="form-control" value={form.nome_fantasia} onChange={(e) => setForm(p => ({ ...p, nome_fantasia: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Telefone</label>
                  <input type="text" className="form-control" value={form.telefone} onChange={(e) => setForm(p => ({ ...p, telefone: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" className="form-control" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowForm(false); setEditing(null); }}>Cancelar</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSalvar}>
                  <i className="fas fa-save"></i> {editing ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <div className="loading-spinner-large"></div>
              <p>Carregando filiais...</p>
            </div>
          ) : filiais.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <i className="fas fa-building" style={{ fontSize: '2.5rem', color: '#ddd', display: 'block', marginBottom: 12 }}></i>
              <p>Nenhuma filial cadastrada</p>
            </div>
          ) : (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>CNPJ</th>
                    <th>Razão Social</th>
                    <th>Nome Fantasia</th>
                    <th>Telefone</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filiais.map(f => (
                    <tr key={f.id}>
                      <td style={{ fontFamily: 'monospace' }}>{f.cnpj}</td>
                      <td style={{ fontWeight: 500 }}>{f.razao_social}</td>
                      <td>{f.nome_fantasia || '-'}</td>
                      <td>{f.telefone || '-'}</td>
                      <td>{f.email || '-'}</td>
                      <td>
                        <span className={`status-badge ${f.ativo ? 'status-montagem-pendente' : 'status-montagem-cancelada'}`}>
                          {f.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                          <button className="btn-edit" onClick={() => handleEditar(f)} title="Editar">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn-delete" onClick={() => handleDeletar(f.id)} title="Excluir">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MontagemPaleteFiliais;
